export type OverlapRect = { left: number; top: number; width: number; height: number }

export type OverlapCheck = {
	/** Área que a aba ocupa (ou ocuparia, se estiver oculta) na viewport. */
	rect: OverlapRect
	/** Elementos empilhados no ponto, do topo para o fundo (document.elementsFromPoint). */
	elementsAt: (x: number, y: number) => Element[]
	/** Largura útil da viewport, sem a barra de rolagem (documentElement.clientWidth). */
	viewportWidth: number
	/** Elementos da própria aba/alça, que nunca contam como sobreposição. */
	isOwn: (el: Element) => boolean
}

// Distância entre os pontos amostrados dentro da aba: menor que qualquer botão ou linha de
// tabela, então nada visível escapa entre dois pontos.
const STEP_PX = 12
// Folga para arredondamento de subpixel ao comparar com as bordas da viewport.
const EDGE_TOLERANCE_PX = 2

export class TabOverlapProvider {
	/**
	 * Diz se algum componente da tela está embaixo da aba lateral.
	 *
	 * Elementos que vão de uma borda à outra da viewport (html, body, wrappers de layout, faixas
	 * de cabeçalho, scrim de diálogo) são fundo: a aba sempre fica "em cima" deles e isso não
	 * atrapalha ninguém. Qualquer outro elemento no espaço da aba é conteúdo sendo coberto.
	 */
	static isOverlapping({ rect, elementsAt, viewportWidth, isOwn }: OverlapCheck): boolean {
		for (const [x, y] of TabOverlapProvider.samplePoints(rect)) {
			for (const el of elementsAt(x, y)) {
				if (isOwn(el) || TabOverlapProvider.isBackground(el, viewportWidth)) continue
				return true
			}
		}
		return false
	}

	static samplePoints(rect: OverlapRect): Array<[number, number]> {
		const axis = (start: number, size: number) => {
			const values: number[] = []
			for (let v = start + 1; v < start + size - 1; v += STEP_PX) values.push(v)
			values.push(start + size - 1)
			return values
		}
		const points: Array<[number, number]> = []
		for (const y of axis(rect.top, rect.height)) {
			for (const x of axis(rect.left, rect.width)) points.push([x, y])
		}
		return points
	}

	private static isBackground(el: Element, viewportWidth: number): boolean {
		const tag = el.tagName.toLowerCase()
		if (tag === 'html' || tag === 'body') return true
		const r = el.getBoundingClientRect()
		return r.left <= EDGE_TOLERANCE_PX && r.right >= viewportWidth - EDGE_TOLERANCE_PX
	}
}
